/**
 * dbBridge — typed IPC seam over the native-Rust on-prem DB bridge.
 *
 * The Tauri desktop app owns ALL DB work (DSN vault, sqlx pool, dialect
 * quirks) in native Rust; this module is the single, typed seam the SPA
 * uses to drive it. Every type mirrors the ``#[derive(Deserialize)]``
 * structs in ``apps/desktop/src-tauri/src/db/bridge.rs`` so the wire shape
 * can't drift.
 *
 * Everything here is desktop-only: the WS bridge (``useEnterpriseDbBridge``)
 * self-gates on ``isTauri`` and each call re-asserts the runtime so a
 * stray web invocation fails loud instead of silently no-op'ing.
 *
 * DSN vault = the OS keychain (Rust ``keyring`` crate — macOS Keychain /
 * Windows Credential Manager / Secret Service), owned SOLELY by Rust.
 * Credentials NEVER leave the local Tauri call: ``dbRegisterDsn`` hands
 * the raw DSN to Rust, which stores it under ``enterprise_db/<handle>``
 * and returns the opaque handle — the handle (only) ends up cloud-side.
 * ``dbExecute`` resolves by handle Rust-side; the one exception is the
 * wizard's pre-registration "test connection", which attaches the DSN
 * inline because no handle exists yet.
 */

import { invoke } from "@tauri-apps/api/core";

import { getEnv } from "@ando/core";

import type {
  DbExecuteArgs,
  DbExecuteResult,
  DbRegisterDsnResult,
  DsnSpec,
} from "./types";

// ── Guard ───────────────────────────────────────────────────────────

/** The on-prem DB bridge only exists inside the Tauri shell. */
export function isEnterpriseDbBridgeAvailable(): boolean {
  return getEnv().isTauri;
}

function assertTauri(fn: string): void {
  if (!getEnv().isTauri) {
    throw new Error(
      `${fn}: the enterprise DB bridge is desktop-only (Tauri). This code path must be gated behind isEnterpriseDbBridgeAvailable().`,
    );
  }
}

/**
 * Substring the Rust ``resolve_dsn`` returns when a ``bridge_device_handle``
 * has no matching entry in the OS keychain. ``queryHandler.mapErrorCode``
 * matches this to bucket the failure as ``PERMISSION`` (user must re-register
 * the connector on this device) instead of the generic ``CONNECTION`` bucket
 * that would burn agentic-loop iterations retrying a fundamentally broken
 * handle. There is NO JS-side retry: the SPA holds no DSN to fall back on.
 *
 * Kept as a substring so it works with the current message shape
 * ``"keyring: no DSN registered for handle {handle}: {err}"`` (other
 * keychain failures share the ``keyring:`` prefix and map to
 * ``KEYCHAIN_LOCKED``). Keep in lockstep with
 * ``apps/desktop/src-tauri/src/db/bridge.rs::resolve_dsn``.
 */
export const DSN_NOT_IN_VAULT_MARKER = "no DSN registered for handle";

// ── Commands ────────────────────────────────────────────────────────

/**
 * Execute one DB operation locally against the customer's database.
 *
 * The Rust side resolves ``bridge_device_handle`` against the OS keychain
 * to recover the DSN, opens (or reuses a cached) ``sqlx`` pool, and returns
 * columns + rows in the same shape as the BE ``DataPage``. Any driver
 * error is mapped to the ``_CONNECTION_ERROR_MARKERS`` vocabulary so the
 * BE agentic loop classifies it correctly (connection vs syntax vs perm).
 */
export async function dbExecute(args: DbExecuteArgs): Promise<DbExecuteResult> {
  assertTauri("dbExecute");
  return invoke<DbExecuteResult>("db_execute_sql", {
    args: {
      bridge_device_handle: args.bridge_device_handle,
      operation: args.operation,
      sql: args.sql,
      // Explicit spread so extra caller-side fields can never slip through
      // to Rust (defence-in-depth around the wire schema).
      params: args.params ? { ...args.params } : undefined,
      timeout_ms: args.timeout_ms,
      max_rows: args.max_rows,
      snapshot_sql: args.snapshot_sql,
      dml_sql: args.dml_sql,
      isolation: args.isolation,
      // Inline DSN, forwarded to Rust ``DbExecuteArgs.dsn`` (typed as
      // ``Option<DsnSpec>`` on the Rust side). Populated ONLY by the
      // wizard's step-2 "test connection" (pre-registration, nothing in
      // the keychain yet) — every other caller omits it so Rust resolves
      // by handle from the keychain / reuses its cached sqlx pool.
      // ``DsnSpec`` here and Rust ``DsnSpec`` share ``dialect``/``host``/
      // ``port``/``database``/``user``/``password``/``options`` verbatim;
      // Rust ``DsnSpec.options`` defaults to an empty map
      // (``#[serde(default)]``) so the field is dropped when the wizard's
      // "advanced options" panel was left closed.
      dsn: args.dsn
        ? {
            dialect: args.dsn.dialect,
            host: args.dsn.host,
            port: args.dsn.port,
            database: args.dsn.database,
            user: args.dsn.user,
            password: args.dsn.password,
            // Spread into a fresh object so a caller can't mutate the
            // record after the invoke (defence-in-depth around the Rust
            // pool key derivation).
            options:
              args.dsn.options && Object.keys(args.dsn.options).length > 0
                ? { ...args.dsn.options }
                : undefined,
          }
        : undefined,
    },
  });
}

/**
 * Register a new DSN in the OS keychain (Rust ``keyring``) and get back
 * the opaque ``bridge_device_handle`` that the BE will use to address it.
 *
 * The raw DSN (host / port / user / password / options) NEVER leaves the
 * customer's device — Rust validates it, stores the JSON spec under
 * ``enterprise_db/<handle>``, and returns just the (Rust-minted) handle.
 *
 * Rotation: call again with the same connection details but a fresh
 * password to mint a new handle; the caller is responsible for updating
 * the cloud ``user_connections`` row and cleaning up the old handle via
 * ``dbDeleteDsn``.
 */
export async function dbRegisterDsn(spec: DsnSpec): Promise<DbRegisterDsnResult> {
  assertTauri("dbRegisterDsn");
  return invoke<DbRegisterDsnResult>("db_register_dsn", {
    spec: {
      dialect: spec.dialect,
      host: spec.host,
      port: spec.port,
      database: spec.database,
      user: spec.user,
      password: spec.password,
      options: spec.options ? { ...spec.options } : undefined,
    },
  });
}

/**
 * Remove a keychain-vaulted DSN by handle — used when the user rotates
 * credentials, removes a bridge connector, or the wizard rolls back a
 * registration the BE refused. The Rust side also drops any cached sqlx
 * pool keyed on this handle, which is why the wizard reuses it to evict
 * the pool its step-2 test opened under a throwaway handle.
 *
 * Tolerant: silently no-ops off-desktop so callers can wire it without
 * a runtime gate.
 */
export async function dbDeleteDsn(bridgeDeviceHandle: string): Promise<void> {
  if (!isEnterpriseDbBridgeAvailable()) return;
  try {
    // Rust command signature is ``db_delete_dsn(handle: String)`` — the
    // Tauri IPC serialiser converts camelCase↔snake_case for the *field
    // name*, but the argument key here has to match what the Rust
    // ``#[command]`` binding expects (single ``handle`` param).
    await invoke<void>("db_delete_dsn", { handle: bridgeDeviceHandle });
  } catch {
    // Best-effort cleanup — the register path already re-derives on next
    // use. Failing here would only spam the user with a modal for what is
    // usually a "vault already empty" harmless case.
  }
}
